import type { CSSProperties } from "react";

interface Props {
  /** The drink's live service_status is "Active" (bartenders see it in Station). */
  isOn: boolean;
  /** Turning on needs an approved version; turning off is always allowed. */
  canToggle: boolean;
  busy?: boolean;
  onToggle: () => void;
  style?: CSSProperties;
}

/**
 * The "On menu / Off menu" switch shared by the Cocktail Specs list and the recipe editor.
 * It shows the drink's *live* service status, and the parent saves the change immediately
 * (bar-admin-set-service-status), so what it shows is what bartenders see.
 */
export default function MenuToggle({ isOn, canToggle, busy = false, onToggle, style }: Props) {
  const title = busy
    ? "Saving…"
    : isOn
      ? "On menu — click to hide from bartenders"
      : canToggle
        ? "Off menu — click to make visible to bartenders"
        : "Needs an approved recipe version to go on menu";
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      aria-label={isOn ? "On menu" : "Off menu"}
      title={title}
      disabled={!canToggle || busy}
      onClick={(e) => {
        e.stopPropagation();
        if (canToggle && !busy) onToggle();
      }}
      style={{
        ...styles.btn,
        opacity: canToggle ? 1 : 0.35,
        cursor: canToggle && !busy ? "pointer" : "default",
        ...style,
      }}
    >
      <span style={{ ...styles.label, color: isOn ? "var(--gold)" : "var(--muted)" }}>
        {busy ? "Saving…" : isOn ? "On menu" : "Off menu"}
      </span>
      <span style={{ ...styles.track, background: isOn ? "var(--gold)" : "var(--border)" }}>
        <span style={{ ...styles.thumb, transform: isOn ? "translateX(14px)" : "translateX(0)" }} />
      </span>
    </button>
  );
}

const styles: Record<string, CSSProperties> = {
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    border: "none",
    padding: 2,
    lineHeight: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
    minWidth: 50,
    textAlign: "right",
  },
  track: {
    display: "inline-flex",
    alignItems: "center",
    width: 30,
    height: 16,
    borderRadius: 8,
    padding: 1,
    transition: "background 0.15s",
    flexShrink: 0,
  },
  thumb: {
    width: 13,
    height: 13,
    borderRadius: "50%",
    background: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
    transition: "transform 0.15s",
    flexShrink: 0,
  },
};
